import React, { ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Switch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useDispatch } from 'react-redux';
import breakpoints, { forDesktop } from '@westwing/ui-kit/breakPoints';
import { useWebviewDetectionHealer } from '../hooks/useWebviewDetectionHealer';
import fontStyles from './GlobalStyles/Fonts';
import Normalizer from './GlobalStyles/Normalizer';
import ClubIntegrationPixel from './ThirdPartyIntegration/ClubIntegrationPixel';
import { paddedMaxScreenWidth } from './GlobalStyles/theme';
import ErrorBoundary from './Generic/ErrorBoundaries';
import NotFound from './NotFound';
import RouteScrollToTop from './RouteScrollToTop';
import useHotjar from './ThirdPartyIntegration/useHotjar';
import useAppTracking from './useAppTracking';
import paths from 'Common/routes/paths';
import routes, { RouteInterface } from 'Common/routes';
import { UserTrackingProvider } from 'Client/hooks/flags/useUserTracking';
import { addNotification } from 'Client/redux/notifications/actions';
import isClient, { cookiesEnabled } from 'Utils/environment';
import useTranslator from 'Client/hooks/useTranslator';
import useConfig from 'AppShell/hooks/useConfig';
import { getPageTypeByUrl, isNativeAppPage } from 'Client/hooks/UrlManager/pageTypeService';
import useSegments from 'Client/hooks/useSegments';
import useFavicons from 'Client/hooks/useFavicons';
import { PageType, SlowDownTestDelayMilliseconds } from 'AppShell/appshell_types';
import SegmentPageViewTracker from 'Common/segment/SegmentPageViewTracker';
import Footer from 'OneWestwingFooter/index';
import useGa from 'Client/hooks/useGa';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { RouterProvider } from '../../appshell/hooks/useRouter';
import { shouldShowClubHeader } from '../../club/utils/shouldShowClubHeader';
import AppHeader from './AppHeader';
import { useBellaSelector } from 'Client/redux/hooks';
import { useSegmentTracking } from 'Common/segment';

interface AppProps {
    host?: string;
    url?: string;
    header?: ReactNode | string;
}

const StyledContentWrapper = styled.div<{ fullWidth?: boolean }>`
    min-height: 100vh;
    position: relative;
    max-width: ${breakpoints.breakpointSemiWideScreens};
    ${({ fullWidth }) => css`
        ${forDesktop`
            max-width: ${fullWidth ? '100%' : paddedMaxScreenWidth}
        `}
    `}
    margin: auto;
`;

const Header = () => {
    const featureToggles = useFeatureToggle();

    if (!isClient()) {
        return null;
    }

    const isClubHeader =
        featureToggles.isOwwHeaderSameBusinessOriginEnabled &&
        shouldShowClubHeader(new URL(window.location.toString()));
    return <AppHeader host={window.location.origin} isClubHeader={isClubHeader} />;
};

// need this serializer as temporary fix, as FT cannot handle numbers properly
export const p100EnableSlowDownTestMap = {
    [SlowDownTestDelayMilliseconds.zeroSeconds]: 0,
    [SlowDownTestDelayMilliseconds.twoSeconds]: 2000,
    [SlowDownTestDelayMilliseconds.threeSeconds]: 3000,
    [SlowDownTestDelayMilliseconds.fourSeconds]: 4000,
};

const App = ({ host = '', url = '', header }: AppProps) => {
    const { appleTouchIcon, favIcon16, favIcon32, favIconSafari } = useFavicons();
    const dispatch = useDispatch();
    const journeyId = useBellaSelector(state => state.appShellLogin.journeyId);
    const t = useTranslator();
    const { trackAppWideVariations } = useAppTracking();
    const { env } = useConfig();
    let { staticHost } = useConfig();
    if (env === 'local') {
        staticHost = '';
    }

    useWebviewDetectionHealer();
    useHotjar();
    useEffect(() => {
        if (!cookiesEnabled) {
            const enableCookiesWarning = t(
                'In your browser, the cookies are not activated, which means that you can not use our site seamlessly. Please activate the cookies in the settings of your browser to use our website.'
            );
            dispatch(addNotification({ message: enableCookiesWarning, displayType: 'warning', isSticky: true }));
        }
    }, [dispatch, t]);

    const { pageType } = getPageTypeByUrl(url, host);
    const title = pageType === PageType.SHIPPING_INFO ? '' : 'Westwing';
    const gaTracking = useGa();
    const segments = useSegments();
    const segmentTracking = useSegmentTracking();

    useEffect(() => {
        trackAppWideVariations(isNativeAppPage(window.location.pathname, segments));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // TODO: these changes will be reverted after the AB/Test.
    const { p100EnableSlowDownTest, p100SmallMobileHeader } = useFeatureToggle();

    useEffect(() => {
        const isBot = !!segments.botSegment;
        const isEligibleForSlowDownTest =
            p100EnableSlowDownTest && !isBot && !isNativeAppPage(window.location.pathname, segments);

        if (p100EnableSlowDownTest && isEligibleForSlowDownTest) {
            const delayInMilliseconds = p100EnableSlowDownTestMap[p100EnableSlowDownTest];
            const trackingLabel = `delay: ${delayInMilliseconds}`;
            gaTracking.trackVariation('p100_marsPerformanceImpactTTI', trackingLabel);
        }

        segmentTracking.trackExperimentViewed({
            experiment_name: 'p100SmallMobileHeader',
            variation_name: `${!!p100SmallMobileHeader} ${journeyId}`,
        });
    }, [p100EnableSlowDownTest, pageType, p100SmallMobileHeader, journeyId]);

    return (
        <ErrorBoundary boundaryName="App" fallbackComponent={<NotFound />}>
            <div className="pageContainer">
                <Helmet style={[{ cssText: fontStyles(staticHost) }]}>
                    <title>{title}</title>
                    <link {...appleTouchIcon} />
                    <link {...favIcon32} />
                    <link {...favIcon16} />
                    <link {...favIconSafari} />
                    <link rel="manifest" href="/manifest.json" />
                </Helmet>
                <Normalizer />
                <Route>
                    {routerProps => (
                        <RouterProvider
                            value={{
                                ...routerProps,
                                host,
                                disableClientsideNavigation: isNativeAppPage(routerProps.location.pathname, segments),
                                isAppWebviewPage: isNativeAppPage(routerProps.location.pathname, segments),
                            }}
                        >
                            <RouteScrollToTop />
                            <SegmentPageViewTracker />
                            <UserTrackingProvider>
                                {header || <Header />}
                                <div className="page">
                                    <StyledContentWrapper
                                        fullWidth={routerProps.location.pathname.startsWith(paths.CHECKOUT)}
                                    >
                                        <Switch>
                                            {routes.map((route: RouteInterface) => (
                                                <Route
                                                    key={JSON.stringify(route.path)}
                                                    exact={route.exact}
                                                    path={route.path}
                                                    render={props => {
                                                        if (props.staticContext) {
                                                            // `status` is user defined key, which we use
                                                            // to setup complete https request handling in express app.
                                                            // @ts-ignore
                                                            // eslint-disable-next-line no-param-reassign
                                                            props.staticContext.status = route.code;
                                                        }
                                                        return <route.component {...props} />;
                                                    }}
                                                />
                                            ))}
                                        </Switch>
                                    </StyledContentWrapper>
                                    <ErrorBoundary boundaryName="Footer" fallbackComponent={null}>
                                        <Footer />
                                    </ErrorBoundary>
                                    <ClubIntegrationPixel />
                                </div>
                            </UserTrackingProvider>
                        </RouterProvider>
                    )}
                </Route>
            </div>
        </ErrorBoundary>
    );
};

export const InitSSR = ({ host = '', location }: { host?: string; location: Location }) => (
    <App host={host} url={location.toString()} />
);

export default App;
